import { mapState, mapMutations, mapActions } from 'vuex';
import { findIndex, clone } from 'lodash';

class UndoAction {
  constructor(component, itemId, meta) {
    this.component = component;
    this.itemId = itemId;
    this.meta = meta;
  }

  // eslint-disable-next-line
  undo() {
    throw new Error('Undo Not Implemented');
  }

  // eslint-disable-next-line
  redo() {
    throw new Error('Redo Not Implemented');
  }

  // eslint-disable-next-line
  undoTooltip() {
    throw new Error('Undo tooltip Not Implemented');
  }

  // eslint-disable-next-line
  redoTooltip() {
    throw new Error('Redo tooltip Not Implemented');
  }
}

class UndoCheckItem extends UndoAction {
  undo() {
    const index = findIndex(this.component.shoppingList.items.checked, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Check item undo failed, item not found.';
      return;
    }

    this.component.uncheckItem(index, this.component.shoppingList.items.checked[index],
      { undoAction: this });
  }

  redo() {
    const index = findIndex(this.component.shoppingList.items.unchecked, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Check item redo failed, item not found.';
      return;
    }

    this.component.checkItem(index, this.component.shoppingList.items.unchecked[index],
      { redoAction: true });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo check item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo check item'; }
}

class UndoUncheckItem extends UndoAction {
  undo() {
    const index = findIndex(this.component.shoppingList.items.unchecked, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Uncheck item undo failed, item not found.';
      return;
    }

    this.component.checkItem(index, this.component.shoppingList.items.unchecked[index],
      { undoAction: this });
  }

  redo() {
    const index = findIndex(this.component.shoppingList.items.checked, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Uncheck item redo failed, item not found.';
      return;
    }

    this.component.uncheckItem(index, this.component.shoppingList.items.checked[index],
      { redoAction: true });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo uncheck item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo uncheck item'; }
}

class UndoMoveItem extends UndoAction {
  undo() {
    const currentList = this.component.shoppingList.items[this.meta.itemListName];
    const index = findIndex(currentList, { id: this.itemId });
    if (index < 0) {
      this.error = 'Undo move item failed, item not found.';
      return;
    }

    if (index !== this.meta.newIndex) {
      /* eslint-disable no-console */
      console.error(`Not good, item found at index ${index}, expected ${this.meta.newIndex}`);
    }

    const optRespList = [];
    currentList.forEach((item, itrIndex) => {
      if (itrIndex === this.meta.index) {
        optRespList.push(clone(currentList[this.meta.newIndex]));
      } else if (itrIndex !== this.meta.newIndex) {
        optRespList.push(clone(item));
      }
    });

    const drag = {
      active: this.meta.itemListName,
      evt: {
        draggedContext: {
          element: {
            id: this.itemId,
          },
          futureIndex: this.meta.index,
          index: this.meta.newIndex,
        },
      },
    };

    this.component.moveItem(this.meta.itemListName, optRespList, drag, { undoAction: this });
  }

  redo() {
    const currentList = this.component.shoppingList.items[this.meta.itemListName];
    const index = findIndex(currentList, { id: this.itemId });
    if (index < 0) {
      this.error = 'Undo move item failed, item not found.';
      return;
    }

    if (index !== this.meta.index) {
      /* eslint-disable no-console */
      console.error(`Not good, item found at index ${index}, expected ${this.meta.newIndex}`);
    }

    const optRespList = [];
    currentList.forEach((item, itrIndex) => {
      if (itrIndex === this.meta.newIndex) {
        optRespList.push(clone(currentList[this.meta.index]));
      } else if (itrIndex !== this.meta.index) {
        optRespList.push(clone(item));
      }
    });

    const drag = {
      active: this.meta.itemListName,
      evt: {
        draggedContext: {
          element: {
            id: this.itemId,
          },
          futureIndex: this.meta.newIndex,
          index: this.meta.index,
        },
      },
    };

    this.component.moveItem(this.meta.itemListName, optRespList, drag,
      { redoAction: true });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo move item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo move item'; }
}

class UndoDeleteItem extends UndoAction {
  undo() {
    this.component.addItem(this.meta.item, this.meta.index, this.meta.itemListName,
      { undoAction: this }).then(({ item }) => {
        // Item id has changed, go through an update any updo/redo items
        this.component.updateUndoRedoItem({
          oldItemId: this.itemId,
          newItemId: item.id,
        });
      });
  }

  redo() {
    this.component.deleteItem(this.meta.index, this.meta.itemListName,
      { redoAction: true });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo delete item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo delete item'; }
}

class UndoAddItem extends UndoAction {
  undo() {
    this.component.deleteItem(this.meta.index, this.meta.itemListName, { undoAction: this });
  }

  redo() {
    this.component.addItem(this.meta.item, this.meta.index, this.meta.itemListName,
      { redoAction: true }).then(({ item }) => {
        // Item id has changed, go through an update any updo/redo items
        this.component.updateUndoRedoItem({
          oldItemId: this.itemId,
          newItemId: item.id,
        });
      });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo add item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo add item'; }
}

class UndoChangeItem extends UndoAction {
  undo() {
    const currentList = this.component.shoppingList.items[this.meta.listName];
    const index = findIndex(currentList, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Change item undo failed, item not found.';
      return;
    }

    this.component.changeItem(this.meta.listName, index, currentList[index],
      this.meta.origDescription, { undoAction: this });
  }

  redo() {
    const currentList = this.component.shoppingList.items[this.meta.listName];
    const index = findIndex(currentList, { id: this.itemId });
    if (index < 0) {
      this.component.error = 'Change item redo failed, item not found.';
      return;
    }

    this.component.changeItem(this.meta.listName, index, currentList[index],
      this.meta.newDescription, { redoAction: true });
  }

  // eslint-disable-next-line
  undoTooltip() { return 'Undo change item'; }

  // eslint-disable-next-line
  redoTooltip() { return 'Redo change item'; }
}

const mixin = {
  computed: {
    ...mapState('shoppingList', ['undoStack', 'redoStack']),
    undoTooltip() {
      if (this.undoStack.length) {
        const undoAction = this.undoStack[this.undoStack.length - 1];
        return undoAction.undoTooltip();
      }

      return 'Undo';
    },

    redoTooltip() {
      if (this.redoStack.length) {
        const undoAction = this.redoStack[this.redoStack.length - 1];
        return undoAction.redoTooltip();
      }

      return 'Redo';
    },
  },
  methods: {
    ...mapMutations('shoppingList',
      ['pushUndo', 'pushRedo', 'updateUndoRedoItem', 'clearUndoRedo']),
    ...mapActions('shoppingList', ['popUndo', 'popRedo']),

    undo() {
      return this.$store.dispatch('shoppingList/popUndo').then((undoAction) => {
        undoAction.undo();
      }).catch((err) => {
        this.error = err.message;
      });
    },

    redo() {
      return this.$store.dispatch('shoppingList/popRedo').then((undoAction) => {
        undoAction.redo();
      }).catch((err) => {
        this.error = err.message;
      });
    },
  },
};

export {
  mixin,
  UndoCheckItem,
  UndoUncheckItem,
  UndoMoveItem,
  UndoDeleteItem,
  UndoAddItem,
  UndoChangeItem,
};



// WEBPACK FOOTER //
// ./src/components/shopping-list/ShoppingListUndoRedo.js